import { html, nothing } from "lit";

import type { AuthPromptReason } from "../auth-prompt";

export type TokenPromptProps = {
  visible: boolean;
  reason: AuthPromptReason;
  tokenValue: string;
  lastError: string | null;
  onTokenChange: (value: string) => void;
  onSubmit: () => void;
  onDismiss: () => void;
};

export function renderTokenPrompt(props: TokenPromptProps) {
  if (!props.visible) return nothing;

  const title =
    props.reason === "insecure-no-token"
      ? "Authentication Required"
      : "Authentication Failed";

  const explanation =
    props.reason === "insecure-no-token"
      ? html`<div class="muted" style="margin-bottom: 12px;">
          This page is served over HTTP, which prevents automatic device
          authentication. Enter your gateway token to connect.
        </div>`
      : html`<div class="muted" style="margin-bottom: 12px;">
          Connection was rejected. Verify your token and try again.
        </div>`;

  return html`
    <div
      class="exec-approval-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Token authentication"
    >
      <div class="exec-approval-card">
        <div class="exec-approval-header">
          <div>
            <div class="exec-approval-title">${title}</div>
            <div class="exec-approval-sub">
              Gateway token is needed to connect
            </div>
          </div>
        </div>
        ${explanation}
        ${props.lastError
          ? html`<div class="callout danger" style="margin-bottom: 12px;">
              ${props.lastError}
            </div>`
          : nothing}
        <label class="field" style="margin-bottom: 16px;">
          <span>Gateway Token</span>
          <input
            type="password"
            .value=${props.tokenValue}
            @input=${(e: Event) =>
              props.onTokenChange((e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter" && props.tokenValue.trim()) {
                props.onSubmit();
              }
            }}
            placeholder="paste your gateway token"
            autofocus
          />
        </label>
        <div class="muted" style="margin-bottom: 12px; font-size: 0.8rem;">
          Tip: add <span class="mono">?token=YOUR_TOKEN</span> to the URL to
          skip this prompt next time.
        </div>
        <div class="exec-approval-actions">
          <button
            class="btn primary"
            ?disabled=${!props.tokenValue.trim()}
            @click=${() => props.onSubmit()}
          >
            Connect
          </button>
          <button class="btn" @click=${() => props.onDismiss()}>Skip</button>
        </div>
      </div>
    </div>
  `;
}
